import { ReactNode } from 'react';
import { BadgeProps } from '@amzn/awsui-components-console';
import {
  LaunchTask,
  WorkStatus,
  BuildRequestStatus,
  AutoSuggestPackageNameOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';

export interface NodeChildren {
  children: ReactNode;
}

export interface FetchedLaunchData {
  data: LaunchTask[];
  loading: boolean;
  // Optional display override: maps a (conference, venue) to the coordinator's canonicalVenueName,
  // falling back to the raw venue name. Display-only (tcx-core-platforms-903 D2).
  venueDisplayName?: (conference?: string, venue?: string) => string;
  // Re-fetches the launch task data for the current scope (tcx-core-platforms-1133). Wired to the
  // table's Refresh button, mirroring the Active packages table.
  onRefresh?: () => void;
}

export interface EmptyStateProps {
  title: string;
  subtitle: string;
  action: ReactNode;
}

export interface Breadcrumb {
  text: string;
  href: string;
}

export const workStatusMessages: Record<
  WorkStatus,
  { text: string; color: BadgeProps['color'] }
> = {
  // Color philosophy:
  // * grey = Task Received, action required by user
  // * yellow (low sev) = Ready for Review, action still required by user
  // * blue = intermediary state, no action required
  // * green = stable state, no action required
  // * red = failure state, will be reset
  TASK_RECEIVED: { text: 'Task Received', color: 'grey' },
  PACKAGE_SUBMITTED_BY_WRITER: {
    text: 'Package Information Updated',
    color: 'blue',
  },
  PACKAGE_STAGING_BUILT: {
    text: 'In Progress',
    color: 'blue',
  },
  PACKAGE_STAGING_BUILD_FAILED: {
    text: 'Package Staging Build Failed',
    color: 'red',
  },
  PACKAGE_STAGING_REMOVED: { text: 'In Progress', color: 'blue' },
  PACKAGE_STAGING_REMOVAL_FAILED: {
    text: 'Package Staging Removal Failed',
    color: 'red',
  },
  PACKAGE_BUILT: { text: 'In Progress', color: 'blue' },
  PACKAGE_BUILD_FAILED: { text: 'Package Build Request Failed', color: 'red' },
  MANIFEST_BUILT: { text: 'Ready for Review', color: 'severity-low' },
  REVIEW_APPROVED: { text: 'Ready to Publish', color: 'green' },
  REVIEW_REJECTED: { text: 'Update Package Information', color: 'red' },
  PUBLISH_SCHEDULED: { text: 'Publishing Scheduled', color: 'green' },
  DELETED: { text: 'Deleted', color: 'red' },
  GROUPED: { text: 'Grouped', color: 'green' },
  RESOLVED: { text: 'Resolved', color: 'green' },
  SHIPPED: { text: 'Shipped', color: 'green' },
} as const;

export enum TraslatedDocumentStatus {
  GREEN = 'Green',
  YELLOW = 'Yellow',
  RED = 'Red',
}

export const buildStatusMessages: Record<
  BuildRequestStatus,
  { text: string; color: BadgeProps['color'] }
> = {
  // Color philosophy:
  // * grey = action required by user
  // * blue = intermediary state, no action required
  // * green = stable state, no action required
  // * red = failure state
  SUBMITTING: { text: '⚡ Submitting your build...', color: 'blue' },
  SUBMIT_FAILED: { text: '🚨 Failed to submit build', color: 'red' },
  WAITING: { text: '⚡ Waiting...', color: 'blue' },
  PRUNABLE: { text: '🤌 Your build can be pruned', color: 'grey' },
  PENDING: { text: '⚡ Build in progress...', color: 'blue' },
  DEFERRED: { text: '🚧 Build deferred', color: 'grey' },
  BLOCKED: { text: '⛔ Build blocked!', color: 'red' },
  IN_PROGRESS: { text: '⚡ Build in progress...', color: 'blue' },
  BLOCKED_ON_SERIALIZATION: { text: '⛔ Build blocked!', color: 'red' },
  CANCELED: { text: '🛑 Build cancelled', color: 'grey' },
  FAILED: { text: '🔥 Build failed 🔥', color: 'red' },
  DONE: { text: 'Build succeeded! 🎉', color: 'green' },
  UNKNOWN: { text: '🤷 Awaiting status...', color: 'blue' },
  SUBMIT_SKIPPED: { text: 'Updated 🎉', color: 'green' },
} as const;

export type PermissionsGroups = 'readonly-user' | 'info-editor';

export type AutoSuggestStatus = 'loading' | 'finished' | 'error' | 'pending';

export type Suggestions = AutoSuggestPackageNameOutput['suggestions'];
